<script setup lang="ts">
import { computed, ref } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { MediaType, PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import Media from "@/views/blocks/shared/section/media/media/Media.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import LightboxDialog from "@/views/blocks/gallery/components/LightboxDialog.vue";
import GalleryButton from "@/views/blocks/gallery/components/GalleryButton.vue";

import type { SimpleThreeColumnsProps } from "./SimpleThreeColumnsProps";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<SimpleThreeColumnsProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

// Lightbox state
const selectedImage = ref<{ src: string; alt: string } | null>(null);
const lightboxDialog = ref<InstanceType<typeof LightboxDialog> | null>(null);

// Lightbox methods
const openLightbox = (image: { src: string; alt: string }) => {
    selectedImage.value = image;
    lightboxDialog.value?.openLightbox();
};

const closeLightbox = () => {
    selectedImage.value = null;
};
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto space-y-8 sm:space-y-16">
                <!-- Header Section -->
                <div
                    class="max-w-2xl flex flex-col gap-4"
                    v-if="
                        !dataValues.tag.isHidden ||
                        !dataValues.title.isHidden ||
                        !dataValues.description.isHidden
                    "
                >
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <Heading
                            :text="dataValues.tag.data.text"
                            :tag="dataValues.tag.data.tag"
                            :text-color="styleValues.brand"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="text-lg opacity-70"
                        />
                    </EditableElement>
                </div>

                <!-- Gallery Grid -->
                <div
                    class="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8"
                    v-if="dataValues.images.data.items.length > 0"
                >
                    <EditableElement
                        v-for="image in dataValues.images.data.items"
                        :key="image.id"
                        :blockId="block.id"
                        :field-name="`images_${image.id}`"
                        :hideable="false"
                    >
                        <GalleryButton
                            v-if="!image.mediaType || image.mediaType === MediaType.IMAGE"
                            :accent-color="styleValues.brand"
                            :aria-label="`View ${image.alt} in lightbox`"
                            @click="openLightbox(image)"
                        >
                            <Image
                                :src="image.src"
                                :alt="image.alt"
                                width="401"
                                height="300"
                                class="w-full object-cover aspect-[4/3] rounded-lg"
                            />
                        </GalleryButton>
                        <div v-else class="w-full aspect-[4/3] rounded-lg overflow-hidden">
                            <Media
                                :data="image"
                                :image-width="401"
                                :image-height="300"
                                :video-width="401"
                                :video-height="300"
                            />
                        </div>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>

        <!-- Lightbox Dialog -->
        <LightboxDialog
            ref="lightboxDialog"
            :selected-image="selectedImage"
            @close="closeLightbox"
        />
    </Section>
</template>
